// HUD оружия: полоса из 9 ячеек (клавиша, иконка, патроны) над статусом
// HEALTH/AMMO + тач-кнопка смены оружия. Разметка и стили создаются
// здесь, чтобы не трогать index.html.

import { WEAPONS, WEAPON_COUNT, WeaponId, weaponDef } from "./catalog";
import { LANG_EVENT, t } from "../i18n";
import type { WeaponState } from "./state";

const STYLE = `
#weapon-bar {
  position: fixed;
  left: 50%;
  top: 10px;
  transform: translateX(-50%);
  display: flex;
  gap: 4px;
  z-index: 6;
  pointer-events: none;
  font-family: var(--font-game, monospace);
  user-select: none;
}
#weapon-bar.hidden { display: none; }
#weapon-bar .wslot {
  position: relative;
  width: 54px;
  height: 36px;
  background: rgba(14, 12, 10, 0.62);
  border: 1px solid rgba(120, 100, 70, 0.45);
  image-rendering: pixelated;
  opacity: 0.35;
  transition: opacity 120ms linear, transform 120ms ease-out, border-color 120ms linear;
}
#weapon-bar .wslot.owned { opacity: 0.78; }
#weapon-bar .wslot.empty .wammo { color: #a55; }
#weapon-bar .wslot.active {
  opacity: 1;
  border-color: #fc6;
  box-shadow: 0 0 10px rgba(255, 190, 80, 0.45), inset 0 0 8px rgba(255, 190, 80, 0.2);
  transform: translateY(3px);
}
#weapon-bar .wslot.flash { border-color: #fff; box-shadow: 0 0 16px rgba(255, 255, 220, 0.8); }
#weapon-bar .wicon {
  position: absolute; inset: 2px 2px 10px 2px;
  background-size: contain; background-repeat: no-repeat; background-position: center;
  image-rendering: pixelated;
}
#weapon-bar .wkey {
  position: absolute; left: 3px; top: 1px;
  font-size: 11px; color: #db8; text-shadow: 0 1px 0 #000;
}
#weapon-bar .wammo {
  position: absolute; right: 3px; bottom: 0;
  font-size: 12px; color: #fec; text-shadow: 0 1px 0 #000;
}
#weapon-bar .wname {
  position: absolute; left: 0; right: 0; top: 13px; text-align: center;
  font-size: 9px; color: #cb9; letter-spacing: 0.05em; text-shadow: 0 1px 0 #000;
}
#weapon-bar .wslot.has-icon .wname { display: none; }
#weapon-title {
  position: fixed;
  left: 50%;
  top: 56px;
  transform: translateX(-50%);
  z-index: 6;
  pointer-events: none;
  font-family: var(--font-game, monospace);
  font-size: 20px;
  letter-spacing: 0.12em;
  color: #fd9;
  text-shadow: 0 0 6px #000, 0 2px 0 #000;
  opacity: 0;
  transition: opacity 400ms ease-out;
}
#weapon-title.show { opacity: 1; transition: opacity 60ms linear; }
#sniper-scope {
  position: fixed; inset: 0; pointer-events: none; z-index: 5; display: none;
  background: radial-gradient(circle at center, rgba(0,0,0,0) 0, rgba(0,0,0,0) 31vmin, rgba(0,0,0,0.92) 32vmin);
}
#sniper-scope.on { display: block; }
#weapon-title .wtitle-hint { font-size: 13px; letter-spacing: 1px; margin-top: 6px; opacity: 0.85; }
.touch-btn--scope.hidden { display: none; }
#sniper-scope::before, #sniper-scope::after {
  content: ""; position: absolute; background: rgba(0,0,0,0.85);
}
#sniper-scope::before { left: 50%; top: calc(50% - 30vmin); width: 2px; height: 60vmin; margin-left: -1px; }
#sniper-scope::after { top: 50%; left: calc(50% - 30vmin); height: 2px; width: 60vmin; margin-top: -1px; }
body.touch-ui #weapon-bar {
  pointer-events: auto; top: max(8px, env(safe-area-inset-top));
  left: max(64px, calc(env(safe-area-inset-left) + 60px)); transform: none; gap: 3px;
}
body.touch-ui #weapon-bar .wslot { width: 42px; height: 32px; }
body.touch-ui #weapon-bar .wslot.active { transform: translateY(2px); }
body.touch-ui #weapon-title { top: 54px; left: 64%; }
.touch-btn--weapon { font-size: 20px; }
`;

export class WeaponHud {
  private bar: HTMLDivElement;
  private slots: HTMLDivElement[] = [];
  private ammoEls: HTMLDivElement[] = [];
  private title: HTMLDivElement;
  private scope: HTMLDivElement;
  private scopeBtn: HTMLButtonElement | null = null;
  private cycleBtn: HTMLButtonElement | null = null;
  private lastKey = "";
  private titleTimer = 0;
  private lastActive = -1;

  constructor(onSlotTap: (id: number) => void, onCycleTap: () => void, onScopeTap: () => void) {
    const st = document.createElement("style");
    st.textContent = STYLE;
    document.head.appendChild(st);

    this.bar = document.createElement("div");
    this.bar.id = "weapon-bar";
    this.bar.className = "hidden";
    for (let i = 0; i < WEAPON_COUNT; i++) {
      const def = weaponDef(i);
      const s = document.createElement("div");
      s.className = "wslot";
      const icon = document.createElement("div");
      icon.className = "wicon";
      const key = document.createElement("div");
      key.className = "wkey";
      key.textContent = String(i + 1);
      const name = document.createElement("div");
      name.className = "wname";
      const ammo = document.createElement("div");
      ammo.className = "wammo";
      s.append(icon, key, name, ammo);
      // Иконка подгружается лениво: нет файла — остаётся подпись.
      const img = new Image();
      img.onload = () => {
        icon.style.backgroundImage = `url(${def.icon})`;
        s.classList.add("has-icon");
      };
      img.src = def.icon;
      s.addEventListener("pointerdown", (e) => {
        if (!document.body.classList.contains("touch-ui")) return;
        e.preventDefault();
        e.stopPropagation();
        onSlotTap(i);
      });
      this.slots.push(s);
      this.ammoEls.push(ammo);
      this.bar.appendChild(s);
    }
    document.body.appendChild(this.bar);

    this.title = document.createElement("div");
    this.title.id = "weapon-title";
    document.body.appendChild(this.title);

    this.scope = document.createElement("div");
    this.scope.id = "sniper-scope";
    document.body.appendChild(this.scope);

    // Тач: кнопка смены оружия рядом с огнём.
    const actions = document.querySelector<HTMLDivElement>("#touch-controls .touch-actions");
    if (actions) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "touch-btn touch-btn--weapon";
      b.textContent = "⇄";
      b.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        b.classList.add("touch-btn--pressed");
        onCycleTap();
      });
      const up = (): void => b.classList.remove("touch-btn--pressed");
      b.addEventListener("pointerup", up);
      b.addEventListener("pointercancel", up);
      const fire = actions.querySelector(".touch-btn--fire");
      actions.insertBefore(b, fire);
      // Оптика снайперки: видна, только когда снайперка в руках.
      const sc = document.createElement("button");
      sc.type = "button";
      sc.className = "touch-btn touch-btn--weapon touch-btn--scope hidden";
      sc.textContent = "◎";
      sc.addEventListener("pointerdown", (e) => {
        e.preventDefault();
        e.stopPropagation();
        onScopeTap();
      });
      actions.insertBefore(sc, fire);
      this.scopeBtn = sc;
      this.cycleBtn = b;
    }
    this.relabel();
    window.addEventListener(LANG_EVENT, () => this.relabel());
  }

  /** Подписи на языке игрока (при создании и при смене языка). */
  private relabel(): void {
    this.slots.forEach((s, i) => {
      const title = t(weaponDef(i).title);
      s.title = `${i + 1} — ${title}`;
      (s.querySelector(".wname") as HTMLDivElement).textContent = title.toUpperCase().slice(0, 7);
    });
    this.cycleBtn?.setAttribute("aria-label", t("Сменить оружие"));
    this.scopeBtn?.setAttribute("aria-label", t("Оптический прицел"));
  }

  setVisible(on: boolean): void {
    this.bar.classList.toggle("hidden", !on);
    if (!on) {
      this.title.classList.remove("show");
      this.setScope(false);
    }
  }

  setScope(on: boolean): void {
    this.scope.classList.toggle("on", on);
  }

  /** Кнопка оптики на телефоне: видна со снайперкой, подсвечена в прицеле. */
  setScopeButton(visible: boolean, on: boolean): void {
    if (!this.scopeBtn) return;
    this.scopeBtn.classList.toggle("hidden", !visible);
    this.scopeBtn.classList.toggle("touch-btn--pressed", on);
  }

  update(st: WeaponState, nowMs: number): void {
    let key = `${st.active}|${st.owned}`;
    for (let i = 0; i < WEAPON_COUNT; i++) key += `|${st.ammo[i]}`;
    if (key !== this.lastKey) {
      this.lastKey = key;
      for (let i = 0; i < WEAPON_COUNT; i++) {
        const s = this.slots[i]!;
        const owned = st.owns(i);
        s.classList.toggle("owned", owned);
        s.classList.toggle("active", i === st.active);
        const a = st.ammoOf(i);
        s.classList.toggle("empty", owned && a === 0);
        this.ammoEls[i]!.textContent = !owned ? "" : a < 0 ? "∞" : String(a);
      }
    }
    if (st.active !== this.lastActive) {
      if (this.lastActive >= 0) {
        this.title.textContent = t(WEAPONS[st.active]?.title ?? "").toUpperCase();
        this.title.classList.add("show");
        this.titleTimer = nowMs + 900;
        if (st.active === WeaponId.Sniper) {
          // Подсказка, как смотреть в оптику (иначе о ПКМ никто не догадывается).
          const hint = document.createElement("div");
          hint.className = "wtitle-hint";
          hint.textContent = document.body.classList.contains("touch-ui")
            ? t("◎ — оптический прицел")
            : t("ПКМ или Z — оптический прицел, колесо — кратность");
          this.title.appendChild(hint);
          this.titleTimer = nowMs + 2600;
        }
      }
      this.lastActive = st.active;
    }
    if (this.titleTimer !== 0 && nowMs > this.titleTimer) {
      this.titleTimer = 0;
      this.title.classList.remove("show");
    }
  }
}
